// Funcionalidades (features) de los comercios: tipos y formato compartidos por el registro autogestionado,
// "Mis funcionalidades" y la consulta de comercios de los bancos.

export interface CatalogFeature {
    id: number;
    code: string;
    name: string;
    description: string | null;
    base: boolean;              // incluida siempre (retail)
    order: number;
    monthlyPrice: number | null;
    currency: string | null;    // CRC / USD
    active?: boolean;           // "Mis funcionalidades": ya la tiene el comercio
}

export interface MerchantFeature {
    code: string;
    name: string;
    monthlyPrice: number | null;
    currency: string | null;
    origin?: string;
    activatedAt?: string | null;
    cancelledAt?: string | null;    // el comercio la canceló
    activeUntil?: string | null;    // 'YYYY-MM-DD': la puede usar hasta ese día (fin del mes en que canceló)
    // Facturación electrónica y contabilidad: se cobran por bloque de uso del mes (monthlyPrice es por bloque)
    usage?: number;                 // documentos emitidos / facturas leídas en el mes
    blockSize?: number;
    blocks?: number;
    amount?: number | null;         // lo que paga este mes: monthlyPrice × bloques
}

export interface MonthlyTotal { currency: string; amount: number; }

export interface MerchantWithFeatures {
    id: number;
    name: string;
    typeLegalId: number | null;
    documentNumber: string | null;
    status: string;
    bankId: number | null;
    countryId: number | null;
    parent: { id: number; name: string | null } | null;
    createdAt: string | null;
    features: MerchantFeature[];
    monthlyTotal: MonthlyTotal[];
}

export const FEATURE_ICON: Record<string, string> = {
    'retail': 'bi-shop',
    'inventory': 'bi-box-seam',
    'electronic-invoice': 'bi-receipt',
    'accounting': 'bi-journal-text',
    'loyalty': 'bi-stars',
    'payment-link': 'bi-link-45deg',
};

export const featureIcon = (code: string) => FEATURE_ICON[code] ?? 'bi-puzzle';

export const ORIGIN_LABEL: Record<string, string> = {
    AUTOGESTION: 'Autogestión',
    API: 'API del banco',
    COMERCIO: 'Agregada por el comercio',
    MIGRACION: 'Migración',
};

// 'YYYY-MM-DD' como fecha local (sin el corrimiento de zona horaria de new Date('YYYY-MM-DD'))
export const formatDay = (day: string | null | undefined) => {
    const [y, m, d] = String(day ?? '').split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d).toLocaleDateString('es-CR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
};

export const formatMoney = (amount: number | null | undefined, currency: string | null | undefined) => {
    if (amount === null || amount === undefined || !currency) return 'Precio por definir';
    try {
        return new Intl.NumberFormat('es-CR', { style: 'currency', currency, maximumFractionDigits: currency === 'CRC' ? 0 : 2 }).format(amount);
    } catch {
        return `${currency} ${amount}`;
    }
};

// Total mensual de varias funcionalidades, por moneda
export const totalOf = (features: { monthlyPrice: number | null; currency: string | null }[]): MonthlyTotal[] => {
    const totals: Record<string, number> = {};
    features.forEach(f => {
        if (f.monthlyPrice === null || !f.currency) return;
        totals[f.currency] = (totals[f.currency] ?? 0) + Number(f.monthlyPrice);
    });
    return Object.entries(totals).map(([currency, amount]) => ({ currency, amount }));
};

export const formatTotals = (totals: MonthlyTotal[]) =>
    totals.length ? totals.map(t => formatMoney(t.amount, t.currency)).join(' + ') : formatMoney(null, null);
